/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable import/no-cycle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
/* eslint no-use-before-define: 0 */
import {
  action,
  thunk,
  computed,
  Computed,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';

import { transformRawArchitectScenes } from '../containers/ArchitectSceneList';
import {
  LoadStatus,
  ArchitectSceneInfo,
  FileInfo,
  // OutputFileInfo,
  PublicationInfo,
  RawPublicationResponse,
} from '../types';
import { fetchWithAuth } from '../utils/requests';
import { fetchWithStatus } from '../utils/state';
// import { updateFrameInUrl } from '../utils/searchParams';

/// ///////////////////////////////////////////////////////////////////////////
// Shared Types And Helpers
/// ///////////////////////////////////////////////////////////////////////////

export type EventType = 'note' | 'warning node';

export const publicationUuidToURL = (uuid: string) =>
  `${window.location.origin}/architect/share/${uuid}`;

const transformRawPublicationResponse = (raw: RawPublicationResponse) => {
  const publicationInfo = {
    linkUrl: publicationUuidToURL(raw.uuid),
    publishedAt: new Date(`${raw.published_at}Z`),
    publishedBy: raw.published_by_name,
    updatedAt: new Date(`${raw.updated_at}Z`),
    updatedBy: raw.updated_by_name,
    title: raw.title,
    uuid: raw.uuid,
  };
  return publicationInfo;
};

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

export type State = {
  loadStatus: LoadStatus;
  architectSceneId: number;
  architectSceneInfo?: ArchitectSceneInfo;

  // Download files
  fileInfo: FileInfo[];
  // This is to mark when all all files to download are all collected. This
  // means that all outputs are finished and files are ready to download. No new
  // files can be added and no point in polling ever again
  filesToDownloadReady: boolean;

  // Publication
  isPublished: boolean;
  publicationInfo?: PublicationInfo;
  publicationLoadStatus: LoadStatus;

  // TODO(pierre) on 2021-04-28: Move to a separate store
  tagNamesToIds?: { [key: string]: number };
  updateTagStatus: LoadStatus;
};

export const defaultState: State = {
  loadStatus: 'notStarted',
  architectSceneId: -1,
  filesToDownloadReady: false,
  isPublished: false,
  fileInfo: [],
  updateTagStatus: 'notStarted',
  publicationLoadStatus: 'notStarted',
};

// type ArchitectSceneData = {
//   architect_sceneInfo: ArchitectSceneInfo;
// };

export interface ArchitectSceneStateModel extends State {
  // Actions & Thunks
  loadArchitectById: EzPzThunk<
    ArchitectSceneStateModel,
    { id: string; isPrivate: boolean }
  >;
  refreshArchitectById: EzPzThunk<
    ArchitectSceneStateModel,
    { id: string; isPrivate: boolean }
  >;
  setArchitectInfo: EzPzAction<ArchitectSceneStateModel, ArchitectSceneInfo>;
  updateTags: EzPzThunk<ArchitectSceneStateModel, string[]>;
  addTag: EzPzAction<ArchitectSceneStateModel, string>;
  removeTag: EzPzAction<ArchitectSceneStateModel, string>;
  setTagNameToIds: EzPzAction<
    ArchitectSceneStateModel,
    { [key: string]: number }
  >;
  setTagStatus: EzPzAction<ArchitectSceneStateModel, LoadStatus>;

  // Download
  setFileInfo: EzPzAction<ArchitectSceneStateModel, FileInfo[]>;
  loadFileInfo: EzPzThunk<
    ArchitectSceneStateModel,
    { id: string; isPrivate: boolean }
  >;
  markDownloadFilesReady: EzPzAction<ArchitectSceneStateModel>;
  sourceDataForDownload: Computed<ArchitectSceneStateModel, FileInfo[]>;
  sourceConfigForDownload: Computed<
    ArchitectSceneStateModel,
    FileInfo | undefined
  >;

  // Publication
  loadPublicationInfo: EzPzThunk<ArchitectSceneStateModel, number>;
  publishArchitectScene: EzPzThunk<ArchitectSceneStateModel, string>;
  updateArchitectScenePublicTitle: EzPzThunk<ArchitectSceneStateModel, string>;
  unpublishArchitectScene: EzPzThunk<ArchitectSceneStateModel>;
  setPublicationInfo: EzPzAction<ArchitectSceneStateModel, PublicationInfo>;
  setIsPublished: EzPzAction<ArchitectSceneStateModel, boolean>;
  setPublicationLoadStatus: EzPzAction<ArchitectSceneStateModel, LoadStatus>;
}

// TODO: Linter to prevent destructuring on immer proxies?
export const model: ArchitectSceneStateModel = {
  ...defaultState,

  // Actions & Thunks
  loadArchitectById: thunk(async (actions, { id, isPrivate }) => {
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private architect_scene with an integer id. Instead received id ${id}`
      );
    }
  }),

  setFileInfo: action((state, files) => {
    state.fileInfo = files;
  }),

  /**
   * Reload only the architect_scene itself
   *
   * There is another method loadArchitectByID that does additional stuff
   */
  refreshArchitectById: thunk(async (actions, { id, isPrivate }) => {
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private architect_scene with an integer id. Instead received id ${id}`
      );
    }

    const url = isPrivate
      ? `/api/v1/architect_scenes/${id}`
      : `/api/v1/published_architect_scenes/${id}`;
    const resp = await fetchWithAuth(url, {
      method: 'GET',
    });
    if (!resp.ok) {
      throw new Error('Bad Response');
    }

    const json = await resp.json();
    const data = transformRawArchitectScenes(json);
    actions.setArchitectInfo(data);
  }),

  updateTags: thunk(async (actions, newTags, { getState }) => {
    const { architectSceneId, architectSceneInfo, tagNamesToIds } = getState();
    if (!architectSceneInfo || !tagNamesToIds) {
      return;
    }

    // TODO(pierre) on 2021-04-28: Switch to PUTting the full array of
    // tag ids when the API supports it
    const oldTags = architectSceneInfo.tags;
    for (const tagName of newTags) {
      if (!oldTags.includes(tagName)) {
        const tagId = tagNamesToIds[tagName];
        await fetchWithStatus(
          actions.setTagStatus,
          // Don't use the callback here since the response is 204
          () => {},
          `/api/v1/architect_scenes/${architectSceneId}/tags/${tagId}`,
          {
            method: 'PUT',
          }
        );
        actions.addTag(tagName);
      }
    }
    for (const tagName of oldTags) {
      if (!newTags.includes(tagName)) {
        const tagId = tagNamesToIds[tagName];
        await fetchWithStatus(
          actions.setTagStatus,
          // Don't use the callback here since the response is 204
          () => {},
          `/api/v1/architect_scenes/${architectSceneId}/tags/${tagId}`,
          {
            method: 'DELETE',
          }
        );
        actions.removeTag(tagName);
      }
    }
  }),

  addTag: action((state, newTag) => {
    if (!state.architectSceneInfo) {
      return;
    }

    if (!state.architectSceneInfo.tags.includes(newTag)) {
      state.architectSceneInfo.tags.push(newTag);
    }
  }),

  removeTag: action((state, newTag) => {
    if (!state.architectSceneInfo) {
      return;
    }

    if (state.architectSceneInfo.tags.includes(newTag)) {
      state.architectSceneInfo.tags = state.architectSceneInfo.tags.filter(
        (t) => t !== newTag
      );
    }
  }),

  setTagStatus: action((state, status) => {
    state.updateTagStatus = status;
  }),

  setArchitectInfo: action((state, architect_sceneInfo: ArchitectSceneInfo) => {
    state.architectSceneInfo = architect_sceneInfo;
  }),

  setTagNameToIds: action((state, mapping) => {
    state.tagNamesToIds = mapping;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Publication
  /// ///////////////////////////////////////////////////////////////////////////
  loadPublicationInfo: thunk(async (actions, id, { getState }) => {
    await fetchWithStatus(
      actions.setPublicationLoadStatus,
      async (raw: RawPublicationResponse) => {
        const publicationInfo = transformRawPublicationResponse(raw);
        actions.setIsPublished(true);
        actions.setPublicationInfo(publicationInfo);
      },
      `/api/v1/architect_scenes/${id}/publication`
    );

    // When we get a 404, then the architect_scene is not published yet, so we set
    // isPublished to false
    const { publicationLoadStatus } = getState();
    if (publicationLoadStatus === 'failed') {
      actions.setIsPublished(false);
    }
  }),

  // Here we process the source files for download
  sourceDataForDownload: computed((state) =>
    state.fileInfo.filter((file: FileInfo) => file.type === 'data')
  ),
  sourceConfigForDownload: computed((state) =>
    state.fileInfo.find((file: FileInfo) => file.type === 'config')
  ),

  publishArchitectScene: thunk(async (actions, title, { getState }) => {
    const { architectSceneId, isPublished } = getState();
    if (isPublished) {
      console.warn('Drive was already published. Will not publish again');
      return;
    }
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify({ title }),
    };
    const endpoint = `/api/v1/architect_scenes/${architectSceneId}/publication`;

    await fetchWithStatus(
      actions.setPublicationLoadStatus,
      async (raw: RawPublicationResponse) => {
        const publicationInfo = transformRawPublicationResponse(raw);
        actions.setPublicationInfo(publicationInfo);
        actions.setIsPublished(true);
      },
      endpoint,
      fetchOptions
    );

    // Fallback in case publication fails
    const { publicationLoadStatus } = getState();
    if (publicationLoadStatus === 'failed') {
      actions.setIsPublished(false);
    }
  }),

  updateArchitectScenePublicTitle: thunk(
    async (actions, title, { getState }) => {
      const { architectSceneId, isPublished } = getState();
      if (!isPublished) {
        console.warn(
          'Can only update public title of data already published. Will not update.'
        );
        return;
      }
      const fetchOptions = {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      };
      const endpoint = `/api/v1/architect_scenes/${architectSceneId}/publication`;

      await fetchWithStatus(
        actions.setPublicationLoadStatus,
        async (raw: RawPublicationResponse) => {
          const publicationInfo = transformRawPublicationResponse(raw);
          actions.setPublicationInfo(publicationInfo);
          actions.setIsPublished(true);
        },
        endpoint,
        fetchOptions
      );
    }
  ),

  unpublishArchitectScene: thunk(async (actions, _, { getState }) => {
    const { architectSceneId, isPublished } = getState();
    if (!isPublished) {
      console.warn('Drive is already private. Will not unpublish');
      return;
    }
    const fetchOptions = {
      method: 'DELETE',
    };
    const endpoint = `/api/v1/architect_scenes/${architectSceneId}/publication`;

    await fetchWithStatus(
      actions.setPublicationLoadStatus,
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      async (raw: RawPublicationResponse) => {
        // Empty callback here because the API will return 204. We
        // instead check if the Status is a success below
      },
      endpoint,
      fetchOptions
    );

    // Fallback in case
    const { publicationLoadStatus } = getState();
    if (publicationLoadStatus === 'success') {
      actions.setIsPublished(false);
    }
  }),

  setPublicationInfo: action((state, info) => {
    state.publicationInfo = info;
  }),

  setIsPublished: action((state, isPublished) => {
    state.isPublished = isPublished;
  }),

  setPublicationLoadStatus: action((state, status) => {
    state.publicationLoadStatus = status;
  }),

  /// ///////////////////////////////////////////////////////////////////////////
  // Fetch source files
  /// ///////////////////////////////////////////////////////////////////////////

  loadFileInfo: thunk(async (actions, { id, isPrivate }) => {
    // if architect_scene is private, the id will be a number and if not, a string.
    if (isPrivate && Number.isNaN(parseInt(id, 10))) {
      throw new Error(
        `You can only load a private architect_scene with an integer id. Instead received id ${id}`
      );
    }
    let FILE_INFO_REQ = '';
    if (isPrivate && parseInt(id, 10)) {
      FILE_INFO_REQ = `/api/v1/architect_scenes/${id}/files/`;
    } else {
      // Published architect_scene
      FILE_INFO_REQ = `/api/v1/published_architect_scenes/${id}/files/`;
    }

    const fetchOptions = {
      method: 'GET',
    };
    try {
      const resp = await fetchWithAuth(FILE_INFO_REQ, fetchOptions);
      if (resp.status === 403) {
        throw new Error(
          'The Ouster Studio does not have access to the storage location of the source files. Cannot load their information'
        );
      }
      if (!resp.ok) {
        throw new Error(
          'Something went wrong when loading architect_scene source file information'
        );
      }
      const filesJson = await resp.json();
      actions.setFileInfo(filesJson);
    } catch (e) {
      if (e instanceof Error) {
        console.warn(e.message);
      } else {
        console.error(e);
      }
    }
  }),

  markDownloadFilesReady: action((state) => {
    state.filesToDownloadReady = true;
  }),
};
